import { z } from 'zod';
import { currencyCodeSchema, isoTimestampSchema, symbolSchema } from './common.js';
/** Use to request the spot price for one symbol. */
export const getPriceParamsSchema = z.object({
    symbol: symbolSchema,
}).strict();
/** Use to request price history for one symbol. */
export const getPriceHistoryParamsSchema = z.object({
    symbol: symbolSchema,
    days: z.union([z.number().int().positive(), z.string().regex(/^\d+$/, 'Expected numeric days')]).optional(),
    currency: currencyCodeSchema.optional(),
}).strict();
/** Use to request the top priced assets. */
export const getTopPricesParamsSchema = z.object({
    limit: z.union([z.number().int().positive(), z.string().regex(/^\d+$/, 'Expected numeric limit')]).optional(),
    currency: currencyCodeSchema.optional(),
}).strict();
/** Use to search for tokens by name or symbol. */
export const searchTokensParamsSchema = z.object({
    q: z.string().min(1, 'Expected non-empty search query'),
    limit: z.union([z.number().int().positive(), z.string().regex(/^\d+$/, 'Expected numeric limit')]).optional(),
}).strict();
/** Use to look up token addresses by symbol. */
export const getTokenAddressesParamsSchema = z.object({
    symbol: symbolSchema,
}).strict();
/** Response shape for a spot price lookup. */
export const priceDataSchema = z.object({
    symbol: symbolSchema,
    name: z.string().min(1),
    priceUsd: z.number(),
    priceEur: z.union([z.number(), z.null()]),
    lastUpdated: isoTimestampSchema,
}).strict();
/** One point in a price history response. */
export const priceHistoryPointSchema = z.object({
    date: z.string().min(1),
    price: z.number(),
}).strict();
/** Response shape for price history. */
export const priceHistoryDataSchema = z.object({
    symbol: symbolSchema,
    name: z.string().min(1),
    currency: currencyCodeSchema,
    prices: z.array(priceHistoryPointSchema),
}).strict();
/** One entry in the top prices response. */
export const topPriceEntrySchema = z.object({
    symbol: symbolSchema,
    name: z.string().min(1),
    price: z.number(),
    currency: currencyCodeSchema,
    chains: z.number().int().nonnegative(),
    lastUpdated: z.string().min(1).optional(),
}).strict();
/** Response shape for top prices. */
export const topPricesDataSchema = z.object({
    count: z.number().int().nonnegative(),
    results: z.array(topPriceEntrySchema),
}).strict();
// searchTokens returns DB-level chain identifiers and per-chain address
// formats just like getTokenAddresses — see tokenAddressEntrySchema below for
// the same rationale. Keep `chain` and `address` free-form strings or any
// non-Spectrum deployment row fails schema validation.
export const tokenSearchChainSchema = z.object({
    chain: z.string().min(1),
    address: z.string().min(1),
    exponent: z.number().int().nonnegative(),
}).strict();
/** One token in a search response. */
export const tokenSearchItemSchema = z.object({
    tokenId: z.string().min(1),
    symbol: symbolSchema,
    name: z.string().min(1),
    priceUsd: z.union([z.number(), z.null()]),
    chains: z.array(tokenSearchChainSchema),
}).strict();
/** Response shape for token search. */
export const tokenSearchDataSchema = z.object({
    count: z.number().int().nonnegative(),
    results: z.array(tokenSearchItemSchema),
}).strict();
/** One chain-specific token address entry. */
// getTokenAddresses echoes rows straight from the token-registry DB, which
// stores DefiLlama-style chain identifiers ("arbitrum-one", "polygon-pos",
// "optimistic-ethereum", ...) and per-chain native address formats (EVM 20-byte,
// Aptos 32-byte, Starknet, etc.). Keep `chain` and `address` as free-form
// strings rather than narrowing to SupportedChainSlug / EVM addressSchema, or
// any non-Spectrum deployment row fails schema validation.
export const tokenAddressEntrySchema = z.object({
    chain: z.string().min(1),
    chainName: z.string().min(1),
    address: z.string().min(1),
    decimals: z.number().int().nonnegative(),
}).strict();
/** Response shape for token address lookups. */
export const tokenAddressesDataSchema = z.object({
    symbol: symbolSchema,
    name: z.string().min(1),
    priceUsd: z.union([z.number(), z.null()]),
    addresses: z.array(tokenAddressEntrySchema),
}).strict();
